/**
 * Short Debriefing Guide — Selection Algorithm
 *
 * Applies business rules to select which UAS + Events to show
 * in the condensed short guide view.
 *
 * NOTE: Canonical implementation is in @orca360/shared (packages/shared/src/utils/shortGuideSelection.ts).
 * This is a local copy because the web app doesn't import from @orca360/shared yet (per CLAUDE.md).
 * Mobile imports from @orca360/shared directly.
 */

import type { UASAnalysis, EventAnalysis } from '../types/debriefing-guide';

export interface ShortGuideDisplayItems {
  uas_items: UASAnalysis[];
  events: EventAnalysis[];
  has_more_uas: boolean;
  has_more_events: boolean;
  total_uas_count: number;
  total_events_count: number;
}

const MAX_UAS_DISPLAY = 3;
const MAX_EVENTS_DISPLAY = 8;
const MIN_COMBINED_ITEMS = 3;

/**
 * Select items for the short guide display.
 *
 * Rules:
 * 1. Show up to 3 UAS items (ordered by severity: Serious > Moderate > Minor)
 * 2. Show up to 8 Events (ordered by event number)
 * 3. Combined total must be >= 3 if enough data exists
 *    - If fewer than 3 UAS, backfill with more events (up to cap)
 *    - If fewer than 3 events, backfill with more UAS (up to cap)
 */
export function selectShortGuideItems(
  allUas: UASAnalysis[],
  allEvents: EventAnalysis[],
): ShortGuideDisplayItems {
  const severityOrder: Record<string, number> = { Serious: 0, Moderate: 1, Minor: 2 };
  const sortedUas = [...allUas].sort(
    (a, b) => (severityOrder[a.severity] ?? 2) - (severityOrder[b.severity] ?? 2),
  );

  let uasToShow = sortedUas.slice(0, MAX_UAS_DISPLAY);
  let eventsToShow = allEvents.slice(0, MAX_EVENTS_DISPLAY);

  const combined = uasToShow.length + eventsToShow.length;
  if (combined < MIN_COMBINED_ITEMS) {
    const deficit = MIN_COMBINED_ITEMS - combined;

    if (eventsToShow.length < allEvents.length) {
      const extra = allEvents.slice(eventsToShow.length, eventsToShow.length + deficit);
      eventsToShow = [...eventsToShow, ...extra];
    }

    const newCombined = uasToShow.length + eventsToShow.length;
    if (newCombined < MIN_COMBINED_ITEMS && uasToShow.length < sortedUas.length) {
      const stillNeeded = MIN_COMBINED_ITEMS - newCombined;
      const extra = sortedUas.slice(uasToShow.length, uasToShow.length + stillNeeded);
      uasToShow = [...uasToShow, ...extra];
    }
  }

  return {
    uas_items: uasToShow,
    events: eventsToShow,
    has_more_uas: allUas.length > uasToShow.length,
    has_more_events: allEvents.length > eventsToShow.length,
    total_uas_count: allUas.length,
    total_events_count: allEvents.length,
  };
}
